/**
 * Date field using a single native date input (`<input type="date">`), which
 * triggers the device's native date picker (calendar on desktop, spinner/wheel on
 * mobile). Stores and accepts values as ISO date strings (YYYY-MM-DD) — the exact
 * format the native control uses — so no manual parsing of day/month/year is needed.
 *
 * Replaces the previous three-select (month/day/year) implementation. The public
 * API is unchanged so existing callers keep working.
 *
 * Use `mode="past"` for dates of birth (allows ~120 years back, up to today).
 * Use `mode="future"` for expiry dates (today up to 20 years forward).
 */

import { cn } from '@elara/client-core/lib/utils';

import { FloatingInput } from './floating-field.js';

/** Oldest selectable date for `past` mode (date of birth), in years before today. */
const DOB_MAX_AGE_YEARS = 120;
/** Furthest selectable date for `future` mode (expiry), in years after today. */
const EXPIRY_MAX_FUTURE_YEARS = 20;

/** ISO `YYYY-MM-DD` for `date`, built from local components to avoid UTC day-shift. */
function toISODate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** True when `value` is a real calendar date in strict `YYYY-MM-DD` form. */
function isValidISODate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

interface DateSelectFieldProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
  required?: boolean;
  showValidation?: boolean;
  /** "past" for DOB (default), "future" for expiry dates */
  mode?: 'past' | 'future';
  className?: string;
}

export function DateSelectField({
  value,
  onChange,
  id,
  label = 'Date',
  required,
  showValidation,
  mode = 'past',
  className,
}: DateSelectFieldProps) {
  const today = new Date();
  const min =
    mode === 'future'
      ? toISODate(today)
      : toISODate(new Date(today.getFullYear() - DOB_MAX_AGE_YEARS, today.getMonth(), today.getDate()));
  const max =
    mode === 'future'
      ? toISODate(new Date(today.getFullYear() + EXPIRY_MAX_FUTURE_YEARS, today.getMonth(), today.getDate()))
      : toISODate(today);

  const isInvalid = value !== '' && !isValidISODate(value);
  // The native min/max only constrain the picker; keyboard entry can still land out of range.
  // ISO YYYY-MM-DD strings compare lexicographically, so plain string comparison is correct here.
  const isOutOfRange = value !== '' && !isInvalid && (value < min || value > max);
  const error = showValidation
    ? required && value === ''
      ? 'Required'
      : isInvalid
        ? 'Enter a valid date'
        : isOutOfRange
          ? value > max
            ? mode === 'future'
              ? 'Date is too far in the future'
              : 'Date must not be in the future'
            : mode === 'future'
              ? 'Date must not be in the past'
              : 'Date is too far in the past'
          : undefined
    : undefined;

  return (
    <FloatingInput
      type="date"
      id={id}
      label={label}
      required={required}
      value={value}
      min={min}
      max={max}
      // An empty native date input still draws text — Safari a greyed-out today's date, Chrome
      // "dd/mm/yyyy" — which reads as a value. While empty and unfocused, that text is hidden and the
      // label rests in the field like any blank input; focusing brings both back so a date can be entered.
      forceFloat={value !== ''}
      error={error}
      onChange={(e) => onChange(e.target.value)}
      className={cn(value === '' && '[&:not(:focus)]:text-transparent', className)}
    />
  );
}
